import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from '../../components/ui-components/Toast';

import Skeleton from "../../components/ui-components/Skeleton";
import { useDelayedTrue } from "../../hooks/useDelayedTrue";

import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import CockpitHeader from "./components/CockpitHeader";
import BudgetDefinitionPanel from "./components/BudgetDefinitionPanel";
import BudgetPeriodsPanel from "./components/BudgetPeriodsPanel";
import BudgetRiskPanel from "./components/BudgetRiskPanel";
import PeriodDrawer from "./components/PeriodDrawer";
import BudgetScopeSheet from "../../components/Budgets/BudgetScopeSheet";
import Modal from "../../components/ui-components/Modal";

import {
  getBudget,
  createBudget,
  updateBudget,
  archiveBudget,
  getBudgetOwners,
  updateBudgetOwners,
  getBudgetScope,
  updateBudgetScope,
  listBudgetDimensions,
  getActiveModelVersion,
  searchNodes,
  listBudgetPeriods,
  submitBudgetActivation,
  pauseBudget,
  resumeBudget,
} from "./api";
import { useBudgetRisk } from "./hooks/useBudgetRisk";
import { mergePeriodForecast } from "./utils/periodForecast";
import { listUsers } from "../users/api";
import { listBudgetPolicies } from "../budget-enforcement/api";
import {
  sumAllocated,
  distributeEven,
  distributeFrontLoaded,
  distributeBackLoaded,
} from "./utils/allocationUtils";

// Allow saving draft when under-allocated so users can save again after first save
const ALLOW_DRAFT_UNDER_ALLOCATED = true;

const BudgetCockpitPage = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [dirty, setDirty] = useState(false);

  // ── Form State ──────────────────────────────────────────────
  const [details, setDetails] = useState({
    name: "",
    budget_type: "hard",
    period_type: "monthly",
    currency: "EUR",
    amount: "",
    description: "",
    policy_id: "",
  });

  const [owners, setOwners] = useState({
    finance_owners: [],
    ops_owners: [],
  });

  const [scope, setScope] = useState({
    scope_mode: "include",
    clauses: [],
  });

  const [periods, setPeriods] = useState([]);

  // Amounts & allocation (authoritative total, strategy)
  const [totalAmountCents, setTotalAmountCents] = useState(0);
  const [allocationStrategy, setAllocationStrategy] = useState("even");
  const [periodEditorOpen, setPeriodEditorOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [scopeSheetOpen, setScopeSheetOpen] = useState(false);
  const [rowVersion, setRowVersion] = useState(0);

  // ── Queries (edit mode only) ────────────────────────────────
  const budgetQuery = useQuery(["budget", id], () => getBudget(id), {
    enabled: isEditMode,
  });
  const ownersQuery = useQuery(["budgetOwners", id], () => getBudgetOwners(id), {
    enabled: isEditMode,
  });
  const scopeQuery = useQuery(["budgetScope", id], () => getBudgetScope(id), {
    enabled: isEditMode,
    staleTime: 30_000,
  });
  const dimensionsQuery = useQuery(["budgetDimensions"], listBudgetDimensions);

  // Risk cards data (right rail). `id` is the saved budget id from the route
  // (edit mode); create mode has none yet, so the hook stays disabled until
  // save navigates to `/budgets/:id`.
  const { data: risk, isLoading: riskLoading } = useBudgetRisk(id);

  const usersQuery = useQuery(["users"], () => listUsers({ limit: 200 }));
  const policiesQuery = useQuery(["budget-policies"], listBudgetPolicies);
  const enforcementPolicies = policiesQuery.data ?? [];

  const userOptions = useMemo(() => {
    const raw = usersQuery.data?.users?.items || [];
    return raw.map((u) => ({
      value: u.id ?? u.ID,
      label: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || String(u.id ?? u.ID ?? ""),
      subtitle: u.function || undefined,
    }));
  }, [usersQuery.data]);

  // Scope panel UI state machine (no "No scope defined" until load confirms empty)
  // In create mode there is no scope fetch; use local scope so the empty state is shown.
  const scopeSource = isEditMode ? scopeQuery.data : scope;
  const isInitialLoading = isEditMode && scopeQuery.isLoading && !scopeQuery.data;
  const isRefreshing = isEditMode && scopeQuery.isFetching && !!scopeQuery.data;
  const hasScope = (scopeSource?.clauses?.length ?? 0) > 0;
  const isEmpty =
    isEditMode
      ? !scopeQuery.isError && scopeQuery.isSuccess && !hasScope
      : !hasScope;
  const isScopeError = isEditMode && !!scopeQuery.error;
  const showScopeSkeleton = useDelayedTrue(isInitialLoading, 200);

  const dimensions = useMemo(() => {
    return (dimensionsQuery.data?.budget_dimensions || []).map((d) => ({
      id: d.id ?? d.ID,
      name: d.name ?? d.Name ?? d.Code ?? "",
    }));
  }, [dimensionsQuery.data]);

  // ── Hydrate form state from queries ─────────────────────────
  useEffect(() => {
    if (isEditMode && budgetQuery.data) {
      // API wraps the response as { budget: { ... } }; unwrap if needed
      const raw = budgetQuery.data?.budget ?? budgetQuery.data;
      const amountCents = raw.amount_cents ?? raw.AmountCents ?? raw.total_amount_cents ?? 0;
      const amountDisplay = amountCents ? String(Number(amountCents) / 100) : "";
      setDetails({
        name: raw.name,
        budget_type: raw.budget_type,
        period_type: raw.period_type,
        currency: raw.currency || "EUR",
        amount: amountDisplay,
        description: raw.description || "",
        policy_id: raw.policy_id != null ? String(raw.policy_id) : "",
      });
      setTotalAmountCents(Number(amountCents) || 0);
      setAllocationStrategy(raw.allocation_strategy ?? "even");
      setRowVersion(raw.row_version ?? 0);
      const rawPeriods = raw.periods ?? [];
      if (Array.isArray(rawPeriods) && rawPeriods.length > 0) {
        const currentStatus = raw.status?.toLowerCase() || "draft";
        setPeriods(
          rawPeriods.map((p) => {
            const key = p.id ?? p.periodKey ?? p.period_key ?? "";
            const cents = Number(p.amount_cents ?? p.amountCents) || 0;
            return {
              id: key,
              periodKey: key,
              label: key,
              amountCents: cents,
              amount: cents / 100,
              status: p.status || currentStatus,
              forecast: null,
            };
          })
        );
      }
    }
  }, [budgetQuery.data, isEditMode]);

  useEffect(() => {
    if (isEditMode && ownersQuery.data) {
      const raw = ownersQuery.data?.owners ?? ownersQuery.data;
      setOwners({
        finance_owners: raw?.finance_owner_user_ids || [],
        ops_owners: raw?.ops_owner_user_ids || [],
      });
    }
  }, [ownersQuery.data, isEditMode]);

  useEffect(() => {
    if (isEditMode && scopeQuery.data) {
      setScope(scopeQuery.data);
    }
  }, [scopeQuery.data, isEditMode]);

  // ── Phase 3: Backend period data (for derived status + period_id) ───
  const backendPeriodsQuery = useQuery({
    queryKey: ["budgetPeriods", id],
    queryFn: () => listBudgetPeriods(id),
    enabled: !!isEditMode,
    staleTime: 30_000,
  });

  // Merge backend period data (derived status, period_id) into local periods, then the
  // per-period projection from the risk payload. Merged here rather than in the effect
  // that builds `periods` because risk resolves independently: baking it into state would
  // leave the column empty until something else happened to re-set the periods.
  const periodsWithStatus = useMemo(() => {
    const backendPeriods = backendPeriodsQuery.data?.periods || [];
    const withBackend =
      backendPeriods.length === 0
        ? periods
        : periods.map((p) => {
            const bp = backendPeriods.find((bp) => bp.label === (p.label ?? p.periodKey));
            if (!bp) return p;
            return {
              ...p,
              period_id: bp.period_id,
              derivedStatus: bp.status,
              active_amount_cents: bp.active_amount_cents,
            };
          });
    return mergePeriodForecast(withBackend, risk);
  }, [periods, backendPeriodsQuery.data, risk]);

  // ── Dirty tracking wrappers ─────────────────────────────────
  const handleDetailsChange = useCallback((newDetails) => {
    setDetails(newDetails);
    setDirty(true);
  }, []);

  const handleOwnersChange = useCallback((newOwners) => {
    setOwners(newOwners);
    setDirty(true);
  }, []);

  // Scope apply from the BudgetScopeSheet
  const handleScopeApply = useCallback(
    async (newScope) => {
      if (isEditMode && id) {
        // In edit mode, persist immediately via API
        try {
          await updateBudgetScope(id, newScope);
          setScope(newScope);
          queryClient.setQueryData(["budgetScope", id], newScope);
          // Scope update bumps the budget row version on the backend (TouchBudget),
          // so we must refetch the budget to get the new version for optimistic concurrency.
          await queryClient.invalidateQueries(["budget", id]);
          queryClient.invalidateQueries(["budgets"]);
          queryClient.invalidateQueries(["financial-control-summary"]);
          toast.success("Scope updated");
        } catch (e) {
          console.error("[handleScopeApply] error:", e);
          toast.error("Failed to update scope");
          return; // keep sheet open on error
        }
      } else {
        // In create mode, just update local state
        setScope(newScope);
        setDirty(true);
      }
      setScopeSheetOpen(false);
    },
    [isEditMode, id, queryClient]
  );

  // Regenerate period amountCents from total and strategy (keeps period id/label/keys)
  const regeneratePeriodAmounts = useCallback(
    (currentPeriods, total, strategy) => {
      if (!currentPeriods.length || total <= 0) return currentPeriods;
      const n = currentPeriods.length;
      let amounts;
      if (strategy === "even") amounts = distributeEven(total, n);
      else if (strategy === "front_loaded") amounts = distributeFrontLoaded(total, n);
      else if (strategy === "back_loaded") amounts = distributeBackLoaded(total, n);
      else return currentPeriods;
      return currentPeriods.map((p, i) => ({
        ...p,
        id: p.id ?? p.periodKey,
        amountCents: amounts[i] ?? 0,
        amount: (amounts[i] ?? 0) / 100,
      }));
    },
    []
  );

  const handleTotalChange = useCallback(
    (cents) => {
      setTotalAmountCents(cents);
      setDetails((prev) => ({
        ...prev,
        amount: cents ? String(cents / 100) : "",
      }));
      setDirty(true);
      setPeriods((prev) => {
        if (prev.length === 0) return prev;
        return regeneratePeriodAmounts(prev, cents, allocationStrategy);
      });
    },
    [allocationStrategy, regeneratePeriodAmounts]
  );

  const handleStrategyChange = useCallback(
    (strategy) => {
      setAllocationStrategy(strategy);
      setDirty(true);
      setPeriods((prev) => {
        if (prev.length === 0) return prev;
        return regeneratePeriodAmounts(prev, totalAmountCents, strategy);
      });
    },
    [totalAmountCents, regeneratePeriodAmounts]
  );

  const handlePeriodsChange = useCallback((newPeriods) => {
    setPeriods(newPeriods);
    setDirty(true);
  }, []);

  const handleEditPeriod = useCallback((period) => {
    setEditingPeriod(period);
    setPeriodEditorOpen(true);
  }, []);

  // Callback for inline draft-mode period amount editing
  const handlePeriodAmountChange = useCallback((period, newAmountCents, applyScope) => {
    const key = period.periodKey ?? period.id;
    setPeriods((prev) =>
      prev.map((p) => {
        const pKey = p.periodKey ?? p.id;
        const shouldUpdate =
          applyScope === "this_and_future"
            ? pKey >= key // lexicographic compare works for "2026-02" and "2026-Q1" style keys
            : pKey === key;
        return shouldUpdate
          ? { ...p, amountCents: newAmountCents, amount: newAmountCents / 100 }
          : p;
      })
    );
    setDirty(true);
  }, []);

  // Resolve drawer period from enriched periods so list edits and backend IDs are reflected
  const periodForSidebar = useMemo(() => {
    if (editingPeriod == null) return null;
    const key = editingPeriod.periodKey ?? editingPeriod.id;
    const live = periodsWithStatus.find((p) => (p.periodKey ?? p.id) === key);
    return live ?? editingPeriod;
  }, [editingPeriod, periodsWithStatus]);

  // ── Save mutation (react-query) ─────────────────────────────
  // Using useMutation guarantees isLoading is set and the spinner renders
  // before the async mutation function starts its network I/O.
  const saveMutation = useMutation(
    async () => {
      // Filter valid scope clauses
      const validClauses = (scope?.clauses || []).filter(
        (c) =>
          c.dimension_id != null &&
          c.dimension_id > 0 &&
          c.node_id != null &&
          c.node_id > 0 &&
          c.clause_group >= 1
      );
      const scopePayload =
        validClauses.length > 0
          ? { scope_mode: scope.scope_mode || "include", clauses: validClauses }
          : { scope_mode: "include", clauses: [] };

      if (isEditMode) {
        const budgetPayload = {
          ...details,
          expected_row_version: rowVersion,
          amount_cents: totalAmountCents,
          total_amount_cents: totalAmountCents,
          allocation_strategy: allocationStrategy,
          policy_id: details.policy_id !== "" ? Number(details.policy_id) : null,
          periods: periods.map((p) => {
            const key = p.id ?? p.periodKey ?? p.period_key ?? "";
            return {
              id: key,
              period_key: key,
              amount_cents: Number(p.amountCents) || Math.round((p.amount ?? 0) * 100),
            };
          }),
        };

        // Step 1: Update budget header + allocation (uses optimistic concurrency)
        await updateBudget(id, budgetPayload);

        // Step 2: Owners and scope run in parallel (no optimistic concurrency,
        //         both acquire row locks so PostgreSQL serialises safely)
        await Promise.all([
          updateBudgetOwners(id, {
            finance_owner_user_ids: owners.finance_owners,
            ops_owner_user_ids: owners.ops_owners,
          }),
          updateBudgetScope(id, scopePayload),
        ]);

        // Step 3: Refetch to get the final row_version after all bumps
        const freshBudget = await getBudget(id);
        return { mode: "edit", freshBudget };
      } else {
        const payload = {
          name: details.name,
          description: details.description || undefined,
          budget_type: details.budget_type,
          period_type: details.period_type,
          currency: details.currency,
          amount_cents: totalAmountCents,
          total_amount_cents: totalAmountCents,
          allocation_strategy: allocationStrategy,
          policy_id: details.policy_id !== "" ? Number(details.policy_id) : null,
          finance_owner_user_ids: Array.isArray(owners.finance_owners)
            ? owners.finance_owners
            : [],
          ops_owner_user_ids: Array.isArray(owners.ops_owners)
            ? owners.ops_owners
            : [],
        };

        const res = await createBudget(payload);
        const budgetId =
          res?.budget?.id ?? res?.budget?.ID ?? res?.id ?? res?.ID;

        if (budgetId != null) {
          try {
            await updateBudgetOwners(budgetId, {
              finance_owner_user_ids: owners.finance_owners,
              ops_owner_user_ids: owners.ops_owners,
            });
          } catch (error_) {
            console.warn("[handleSave] secondary updates failed", error_);
          }
          if (validClauses.length > 0) {
            try {
              await updateBudgetScope(budgetId, scopePayload);
            } catch (error_) {
              console.warn("[handleSave] secondary updates failed", error_);
            }
          }
          // Save periods via updateBudget (PUT) if any were generated
          if (periods.length > 0) {
            try {
              const createdBudget = await getBudget(budgetId);
              const created = createdBudget?.budget ?? createdBudget;
              const createdVersion = created?.row_version ?? 1;

              await updateBudget(budgetId, {
                expected_row_version: createdVersion,
                total_amount_cents: totalAmountCents,
                allocation_strategy: allocationStrategy,
                periods: periods.map((p) => {
                  const key = p.id ?? p.periodKey ?? p.period_key ?? "";
                  return {
                    id: key,
                    period_key: key,
                    amount_cents: Number(p.amountCents) || Math.round((p.amount ?? 0) * 100),
                  };
                }),
              });
            } catch (error_) {
              console.warn("[handleSave] period save after create failed", error_);
            }
          }
        }
        return { mode: "create", budgetId };
      }
    },
    {
      onSuccess: (data) => {
        // Refresh the executive dashboard's budget KPI after any save.
        queryClient.invalidateQueries(["financial-control-summary"]);
        if (data.mode === "edit") {
          const fresh = data.freshBudget?.budget ?? data.freshBudget;
          const newVersion = fresh?.row_version;
          if (newVersion != null) {
            setRowVersion(newVersion);
          }
          toast.success("Budget updated successfully");
          queryClient.invalidateQueries(["budget", id]);
          queryClient.invalidateQueries(["budgetOwners", id]);
          queryClient.invalidateQueries(["budgetScope", id]);
          queryClient.invalidateQueries(["budgets"]);
          setDirty(false);
        } else {
          queryClient.invalidateQueries(["budgets"]);
          if (data.budgetId != null) {
            toast.success("Budget created successfully");
            navigate(`/budgets/${data.budgetId}`);
          } else {
            toast.success("Budget created");
            navigate("/budgets");
          }
        }
      },
      onError: (e) => {
        console.error("[handleSave] error:", e);
        // Prefer the backend's message (authenticatedFetch attaches error.message
        // from the server body) so validation failures are meaningful; fall back to
        // a generic message only for opaque HTTP errors.
        const backendMsg =
          e?.message && !/^HTTP error/i.test(e.message) ? e.message : null;
        const message =
          backendMsg || (isEditMode ? "Failed to update budget" : "Failed to create budget");
        toast.error(message);
        if (e?.status === 404 && isEditMode) {
          navigate("/budgets");
        }
      },
    }
  );

  const isSaving = saveMutation.isLoading;

  const handleSave = useCallback(() => {
    if (!details?.name?.trim()) {
      toast.error("Please enter a budget name before saving.");
      return;
    }
    const allocatedSum = sumAllocated(periods);
    if (allocatedSum > totalAmountCents) {
      toast.error("Over-allocated. Reduce period amounts to match total.");
      return;
    }
    if (allocatedSum < totalAmountCents && !ALLOW_DRAFT_UNDER_ALLOCATED) {
      toast.error("Under-allocated. Allocate the full total to save.");
      return;
    }
    saveMutation.mutate();
  }, [details, periods, totalAmountCents, saveMutation]);

  const handleSaveAndApprove = useCallback(() => {
    // For now same as save draft -- approval flow to be added
    handleSave();
  }, [handleSave]);

  // ── Derived allocation state (for save blocking and reconciliation) ──
  const allocated = useMemo(() => sumAllocated(periods), [periods]);
  const isOver = allocated > totalAmountCents;
  const isUnder = totalAmountCents > 0 && allocated < totalAmountCents;
  const isBalanced = totalAmountCents > 0 && allocated === totalAmountCents;

  // ── Save/approve requirements: scope, periods, amounts, 100% allocated ──
  const scopeSourceForValidation = isEditMode ? scopeQuery.data : scope;
  const hasValidScope = useMemo(() => {
    const clauses = scopeSourceForValidation?.clauses ?? [];
    const isValidClause = (c) =>
      (c.clause_group != null && c.clause_group >= 1) &&
      (c.dimension_id != null && c.dimension_id > 0) &&
      (c.node_id != null && c.node_id > 0) &&
      (c.operator != null && String(c.operator).trim() !== "");
    return clauses.some(isValidClause);
  }, [scopeSourceForValidation]);
  const hasPeriods = periods.length > 0;
  const allPeriodsHaveAmount = hasPeriods && periods.every(
    (p) => (Number(p.amountCents) ?? 0) > 0
  );
  const requirementsMet =
    hasValidScope && hasPeriods && allPeriodsHaveAmount && isBalanced;

  const saveDraftDisabled = isSaving || isOver;
  const saveAndApproveDisabled = isSaving || isOver || isUnder || !requirementsMet;

  const saveDraftTooltip = isOver
    ? "Over-allocated. Reduce period amounts to match total."
    : null;
  const saveAndApproveTooltip = isOver
    ? "Over-allocated. Reduce period amounts to match total."
    : !hasValidScope
    ? "Add at least one scope clause (dimension, node/value, and operator)."
    : !hasPeriods
    ? "Add at least one budget period."
    : !allPeriodsHaveAmount
    ? "Define an amount for every period."
    : isUnder
    ? "Under-allocated. Allocate the full total before submitting for approval."
    : null;
  const showDraftUnderAllocatedHint =
    ALLOW_DRAFT_UNDER_ALLOCATED && isUnder && !isOver;

  // ── Node search helper ──────────────────────────────────────
  const handleNodeSearch = useCallback(async (dimensionId, query) => {
    try {
      const dimId = dimensionId != null ? Number(dimensionId) : null;
      if (dimId == null || Number.isNaN(dimId)) return { nodes: [] };

      const versionRes = await getActiveModelVersion(dimId);
      const version =
        versionRes?.cost_model_version ?? versionRes?.data?.cost_model_version;
      const versionId = version?.id ?? version?.ID;
      if (versionId == null) return { nodes: [] };

      const nodesRes = await searchNodes({
        modelVersionId: Number(versionId),
        q: query != null ? String(query) : "",
        limit: 20,
      });
      const raw = Array.isArray(nodesRes?.nodes) ? nodesRes.nodes : [];
      const mapped = raw.map((n) => ({
        id: n.node_id ?? n.NodeID ?? n.id,
        label:
          n.name ??
          n.Name ??
          n.label ??
          n.Code ??
          String(n.node_id ?? n.NodeID ?? n.id ?? ""),
      }));
      return { nodes: mapped };
    } catch (e) {
      console.error("[handleNodeSearch] error:", e);
      return { nodes: [] };
    }
  }, []);

  // ── Unsaved changes guard ───────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Note: click-outside handling for the Period Drawer is managed by RightSheet.

  // ── Budget status transition mutations ─────────────────────
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, title: "", description: "", icon: null });

  const submitActivationMutation = useMutation(() => submitBudgetActivation(id), {
    onMutate: () => setIsTransitioning(true),
    onSuccess: (data) => {
      toast.success("Budget activated successfully.");
      if (data) {
        queryClient.setQueryData(["budget", id], data);
      }
      queryClient.invalidateQueries(["budgets"]);
      queryClient.invalidateQueries(["budgetPeriods", id]);
      queryClient.invalidateQueries(["financial-control-summary"]);
      setDirty(false);
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.message || "Failed to activate budget.";
      toast.error(msg);
    },
    onSettled: () => setIsTransitioning(false),
  });

  const pauseMutation = useMutation(() => pauseBudget(id), {
    onMutate: () => setIsTransitioning(true),
    onSuccess: (data) => {
      toast.success("Budget paused.");
      if (data) {
        queryClient.setQueryData(["budget", id], data);
      }
      queryClient.invalidateQueries(["budgets"]);
      queryClient.invalidateQueries(["budgetPeriods", id]);
      queryClient.invalidateQueries(["financial-control-summary"]);
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.message || "Failed to pause budget.";
      toast.error(msg);
    },
    onSettled: () => setIsTransitioning(false),
  });

  const resumeMutation = useMutation(() => resumeBudget(id), {
    onMutate: () => setIsTransitioning(true),
    onSuccess: (data) => {
      toast.success("Budget resumed.");
      if (data) {
        queryClient.setQueryData(["budget", id], data);
      }
      queryClient.invalidateQueries(["budgets"]);
      queryClient.invalidateQueries(["budgetPeriods", id]);
      queryClient.invalidateQueries(["financial-control-summary"]);
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.message || "Failed to resume budget.";
      toast.error(msg);
    },
    onSettled: () => setIsTransitioning(false),
  });

  const archiveMutation = useMutation(() => archiveBudget(id), {
    onMutate: () => setIsTransitioning(true),
    onSuccess: (data) => {
      toast.success("Budget archived.");
      if (data) {
        queryClient.setQueryData(["budget", id], data);
      }
      queryClient.invalidateQueries(["budgets"]);
      queryClient.invalidateQueries(["budgetPeriods", id]);
      queryClient.invalidateQueries(["financial-control-summary"]);
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.message || "Failed to archive budget.";
      toast.error(msg);
    },
    onSettled: () => setIsTransitioning(false),
  });

  const closeConfirmModal = useCallback(() => {
    setConfirmModal({ open: false, action: null, title: "", description: "", icon: null });
  }, []);

  const handleSubmitActivation = useCallback(() => {
    setConfirmModal({
      open: true,
      action: () => { submitActivationMutation.mutate(); },
      title: "Activate budget?",
      description: "Period version changes will become enforceable once this budget is active.",
      icon: icon({ name: "circle-check", style: "solid" }),
      confirmButton: "Activate",
    });
  }, [submitActivationMutation]);

  const handlePause = useCallback(() => {
    setConfirmModal({
      open: true,
      action: () => { pauseMutation.mutate(); },
      title: "Pause budget?",
      description: "Period versions cannot be activated while the budget is paused.",
      icon: icon({ name: "circle-pause", style: "solid" }),
      confirmButton: "Pause",
    });
  }, [pauseMutation]);

  const handleResume = useCallback(() => {
    setConfirmModal({
      open: true,
      action: () => { resumeMutation.mutate(); },
      title: "Resume budget?",
      description: "This will resume the budget and allow period version changes again.",
      icon: icon({ name: "circle-play", style: "solid" }),
      confirmButton: "Resume",
    });
  }, [resumeMutation]);

  const handleArchive = useCallback(() => {
    setConfirmModal({
      open: true,
      action: () => { archiveMutation.mutate(); },
      title: "Archive budget?",
      description: "This budget will stop applying to future spend. Past data is preserved. This action cannot be easily undone.",
      icon: icon({ name: "box-archive", style: "solid" }),
      confirmButton: "Archive",
      alertIcon: true,
    });
  }, [archiveMutation]);

  // ── Derived state ───────────────────────────────────────────
  const layoutMode = hasPeriods ? "time_cockpit" : "definition";
  const rawBudget = budgetQuery.data?.budget ?? budgetQuery.data;
  const budgetStatus = rawBudget?.status?.toLowerCase() || "draft";

  // Loading state
  if (isEditMode && budgetQuery.isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={400} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Sticky header */}
      <CockpitHeader
        isEditMode={isEditMode}
        budgetName={details.name}
        status={budgetStatus}
        isSaving={isSaving}
        dirty={dirty}
        onSaveDraft={handleSave}
        onSaveAndApprove={handleSaveAndApprove}
        onSubmitActivation={isEditMode && budgetStatus === "draft" ? handleSubmitActivation : undefined}
        onPause={isEditMode && budgetStatus === "active" ? handlePause : undefined}
        onResume={isEditMode && budgetStatus === "paused" ? handleResume : undefined}
        onArchive={isEditMode && ["draft", "active", "paused"].includes(budgetStatus) ? handleArchive : undefined}
        saveDraftDisabled={saveDraftDisabled}
        saveAndApproveDisabled={saveAndApproveDisabled}
        saveDraftTooltip={saveDraftTooltip}
        saveAndApproveTooltip={saveAndApproveTooltip}
        showDraftUnderAllocatedHint={showDraftUnderAllocatedHint}
        isTransitioning={isTransitioning}
      />

      {/* Content area: scrollable grid + period editor sidebar (sticks to header) */}
      <div className="flex-1 flex min-h-0 relative">
        {/* 3-column grid - scrollable */}
        <div
          className="flex-1 overflow-auto p-6 min-w-0"
          {...(scopeSheetOpen ? { inert: "" } : {})}
        >
          <div
            className="h-full"
            style={{
              display: "grid",
              gap: "1.5rem",
              alignItems: "start",
              // Once periods exist the page has one workspace and two rails. Fractional
              // side columns grew with the viewport, so on a wide screen the standing
              // facts and the risk context claimed as much room as the thing being
              // decided. Fixed rails cap them and give every extra pixel to the
              // authorization and its periods. Definition mode keeps its balanced
              // thirds: there is no table yet, and the definition IS the workspace.
              gridTemplateColumns:
                layoutMode === "definition"
                  ? "1fr 1.03fr 1fr"
                  : "240px minmax(0, 1fr) 220px",
              transition:
                "grid-template-columns 250ms cubic-bezier(0.4, 0.0, 0.2, 1)",
            }}
          >
            {/* Left: Definition & Governance */}
            <div className="overflow-auto min-w-0 w-full">
              <BudgetDefinitionPanel
                details={details}
                onDetailsChange={handleDetailsChange}
                autoFocusName={!isEditMode}
                owners={owners}
                onOwnersChange={handleOwnersChange}
                userOptions={userOptions}
                scope={scopeSource ?? scope}
                dimensions={dimensions}
                onOpenScopeSheet={() => setScopeSheetOpen(true)}
                scopeQueryStatus={{
                  showSkeleton: showScopeSkeleton,
                  isRefreshing,
                  hasScope,
                  isEmpty,
                  isError: isScopeError,
                }}
                scopeRefetch={scopeQuery.refetch}
                enforcementPolicies={enforcementPolicies}
              />
            </div>

            {/* Center: Periods (primary surface) */}
            <div className="relative overflow-hidden">
              <BudgetPeriodsPanel
                periods={periodsWithStatus}
                onPeriodsChange={handlePeriodsChange}
                periodType={details.period_type}
                totalAmountCents={totalAmountCents}
                allocationStrategy={allocationStrategy}
                onTotalChange={handleTotalChange}
                onStrategyChange={handleStrategyChange}
                currency={details.currency}
                onCurrencyChange={(c) => handleDetailsChange({ ...details, currency: c })}
                regeneratePeriodAmounts={regeneratePeriodAmounts}
                onEditPeriod={handleEditPeriod}
              />
            </div>

            {/* Right: Preview & Risk */}
            <div className="overflow-auto">
              <BudgetRiskPanel
                hasPeriods={hasPeriods}
                risk={risk}
                riskLoading={riskLoading}
                currency={details.currency}
              />
            </div>
          </div>
        </div>

        {/* Phase 3: Period drawer with version history and draft editor */}
        <PeriodDrawer
          period={periodForSidebar}
          open={periodEditorOpen}
          onClose={() => {
            setPeriodEditorOpen(false);
            setEditingPeriod(null);
          }}
          currency={details.currency}
          budgetId={id}
          budgetStatus={budgetStatus}
          onAmountChange={handlePeriodAmountChange}
        />

        {/* Scope builder sheet (right-side overlay) */}
        <BudgetScopeSheet
          open={scopeSheetOpen}
          savedScope={scope}
          onApply={handleScopeApply}
          onCancel={() => setScopeSheetOpen(false)}
          dimensions={dimensions}
          nodeSearch={handleNodeSearch}
        />
      </div>

      {/* Confirmation modal for budget status transitions */}
      <Modal
        show={confirmModal.open}
        close={closeConfirmModal}
        title={confirmModal.title}
        description={confirmModal.description}
        confirmButton={confirmModal.confirmButton}
        confirmAction={() => {
          if (confirmModal.action) confirmModal.action();
          closeConfirmModal();
        }}
        icon={confirmModal.icon}
        alertIcon={confirmModal.alertIcon}
      />
    </div>
  );
};

export default BudgetCockpitPage;
